import React from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonChip,
  IonText,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
} from '@ionic/react';
import {
  closeOutline,
  personOutline,
  businessOutline,
  mailOutline,
  callOutline,
  cardOutline,
  cardOutline as bankOutline,
  globeOutline,
  informationCircleOutline,
} from 'ionicons/icons';
import { Supplier, Sale, Ticket } from '../types';
import { getCurrencySymbol, DEFAULT_CURRENCY } from '../utils/currencyConstants';
import { format } from 'date-fns';

interface SourceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceType: 'supplier' | 'buyer' | 'site' | 'private';
  sourceData: Supplier | Sale | Ticket | null;
  context: 'inventory' | 'sale';
}

const SourceDetailsModal: React.FC<SourceDetailsModalProps> = ({
  isOpen,
  onClose,
  sourceType,
  sourceData,
  context,
}) => {
  const renderSupplierDetails = (supplier: Supplier) => (
    <IonContent>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center">
            <IonIcon icon={businessOutline} className="mr-2" />
            Supplier Details
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonList>
            <IonItem>
              <IonIcon icon={personOutline} slot="start" />
              <IonLabel>
                <h3>Name</h3>
                <p>{supplier.name || 'Unknown Supplier'}</p>
              </IonLabel>
            </IonItem>

            {supplier.contactName && (
              <IonItem>
                <IonIcon icon={personOutline} slot="start" />
                <IonLabel>
                  <h3>Contact Person</h3>
                  <p>{supplier.contactName}</p>
                </IonLabel>
              </IonItem>
            )}

            {supplier.company && (
              <IonItem>
                <IonIcon icon={businessOutline} slot="start" />
                <IonLabel>
                  <h3>Company</h3>
                  <p>{supplier.company}</p>
                </IonLabel>
              </IonItem>
            )}

            {supplier.email && (
              <IonItem button onClick={() => window.open(`mailto:${supplier.email}`)}>
                <IonIcon icon={mailOutline} slot="start" />
                <IonLabel>
                  <h3>Email</h3>
                  <p>{supplier.email}</p>
                </IonLabel>
              </IonItem>
            )}

            {supplier.phone && (
              <IonItem button onClick={() => window.open(`tel:${supplier.phone}`)}>
                <IonIcon icon={callOutline} slot="start" />
                <IonLabel>
                  <h3>Phone</h3>
                  <p>{supplier.phone}</p>
                </IonLabel>
              </IonItem>
            )}

            {supplier.calculatedBalance !== undefined && (
              <IonItem>
                <IonIcon icon={cardOutline} slot="start" />
                <IonLabel>
                  <h3>Balance</h3>
                  <p className={supplier.calculatedBalance >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {getCurrencySymbol(DEFAULT_CURRENCY)}{supplier.calculatedBalance.toFixed(2)}
                  </p>
                </IonLabel>
              </IonItem>
            )}

            {supplier.salesCount !== undefined && (
              <IonItem>
                <IonIcon icon={informationCircleOutline} slot="start" />
                <IonLabel>
                  <h3>Total Sales</h3>
                  <p>{supplier.salesCount} transactions</p>
                </IonLabel>
              </IonItem>
            )}
          </IonList>

          {/* Bank Details */}
          {(supplier.bankName || supplier.accountNumber || supplier.paypalEmail) && (
            <IonCard className="mt-4">
              <IonCardHeader>
                <IonCardTitle className="flex items-center text-lg">
                  <IonIcon icon={bankOutline} className="mr-2" />
                  Payment Details
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList>
                  {supplier.bankName && (
                    <IonItem>
                      <IonLabel>
                        <h3>Bank</h3>
                        <p>{supplier.bankName}</p>
                      </IonLabel>
                    </IonItem>
                  )}

                  {supplier.accountName && (
                    <IonItem>
                      <IonLabel>
                        <h3>Account Name</h3>
                        <p>{supplier.accountName}</p>
                      </IonLabel>
                    </IonItem>
                  )}

                  {supplier.accountNumber && (
                    <IonItem>
                      <IonLabel>
                        <h3>Account Number</h3>
                        <p>{supplier.accountNumber}</p>
                      </IonLabel>
                    </IonItem>
                  )}

                  {supplier.paypalEmail && (
                    <IonItem>
                      <IonLabel>
                        <h3>PayPal Email</h3>
                        <p>{supplier.paypalEmail}</p>
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              </IonCardContent>
            </IonCard>
          )}

          {supplier.notes && (
            <IonCard className="mt-4">
              <IonCardHeader>
                <IonCardTitle className="text-lg">Notes</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="text-gray-700 dark:text-gray-300">{supplier.notes}</p>
              </IonCardContent>
            </IonCard>
          )}
        </IonCardContent>
      </IonCard>
    </IonContent>
  );

  const renderBuyerDetails = (sale: Sale) => (
    <IonContent>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center">
            <IonIcon icon={personOutline} className="mr-2" />
            Buyer Details
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonList>
            {sale.buyerName && (
              <IonItem>
                <IonIcon icon={personOutline} slot="start" />
                <IonLabel>
                  <h3>Name</h3>
                  <p>{sale.buyerName}</p>
                </IonLabel>
              </IonItem>
            )}

            {sale.buyerEmail && (
              <IonItem button onClick={() => window.open(`mailto:${sale.buyerEmail}`)}>
                <IonIcon icon={mailOutline} slot="start" />
                <IonLabel>
                  <h3>Email</h3>
                  <p>{sale.buyerEmail}</p>
                </IonLabel>
              </IonItem>
            )}

            {sale.buyerPhone && (
              <IonItem button onClick={() => window.open(`tel:${sale.buyerPhone}`)}>
                <IonIcon icon={callOutline} slot="start" />
                <IonLabel>
                  <h3>Phone</h3>
                  <p>{sale.buyerPhone}</p>
                </IonLabel>
              </IonItem>
            )}

            <IonItem>
              <IonIcon icon={cardOutline} slot="start" />
              <IonLabel>
                <h3>Payment Method</h3>
                <p className="capitalize">{sale.paymentMethod}</p>
              </IonLabel>
            </IonItem>

            <IonItem>
              <IonIcon icon={informationCircleOutline} slot="start" />
              <IonLabel>
                <h3>Sale Date</h3>
                <p>{format(new Date(sale.saleDate), 'MMM dd, yyyy')}</p>
              </IonLabel>
            </IonItem>

            <IonItem>
              <IonIcon icon={informationCircleOutline} slot="start" />
              <IonLabel>
                <h3>Total Amount</h3>
                <p className="text-green-600 font-semibold">
                  {getCurrencySymbol(sale.currency || DEFAULT_CURRENCY)}{sale.totalAmount.toFixed(2)}
                </p>
              </IonLabel>
            </IonItem>

            {sale.orderNumber && (
              <IonItem>
                <IonIcon icon={informationCircleOutline} slot="start" />
                <IonLabel>
                  <h3>Order Number</h3>
                  <p>{sale.orderNumber}</p>
                </IonLabel>
              </IonItem>
            )}
          </IonList>

          {sale.notes && (
            <IonCard className="mt-4">
              <IonCardHeader>
                <IonCardTitle className="text-lg">Notes</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <p className="text-gray-700 dark:text-gray-300">{sale.notes}</p>
              </IonCardContent>
            </IonCard>
          )}
        </IonCardContent>
      </IonCard>
    </IonContent>
  );

  const renderSiteDetails = (data: Ticket | Sale) => (
    <IonContent>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center">
            <IonIcon icon={globeOutline} className="mr-2" />
            Platform Details
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonList>
            <IonItem>
              <IonIcon icon={globeOutline} slot="start" />
              <IonLabel>
                <h3>Platform/Site</h3>
                <p className="capitalize">
                  {'source' in data ? data.source : 
                   (data as Ticket).supplier || 
                   (typeof (data as Ticket).supplierId === 'object' ? 
                     ((data as Ticket).supplierId as any)?.name : 
                     'Unknown Platform')}
                </p>
              </IonLabel>
            </IonItem>

            {'source' in data && data.source && (
              <IonItem>
                <IonChip 
                  color={
                    data.source === 'livefootballtickets' ? 'primary' :
                    data.source === 'stubhub' ? 'secondary' :
                    data.source === 'footballticketnet' ? 'tertiary' :
                    'medium'
                  }
                >
                  {data.source}
                </IonChip>
              </IonItem>
            )}

            <IonItem>
              <IonIcon icon={informationCircleOutline} slot="start" />
              <IonLabel>
                <h3>Type</h3>
                <p>Online Platform</p>
              </IonLabel>
            </IonItem>
          </IonList>

          <IonText className="block mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This is an automated purchase/sale from an online ticketing platform. 
              Contact details are typically not available for platform transactions.
            </p>
          </IonText>
        </IonCardContent>
      </IonCard>
    </IonContent>
  );

  const renderPrivateDetails = () => (
    <IonContent>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center">
            <IonIcon icon={personOutline} className="mr-2" />
            Private Transaction
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonText className="block p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This is a private transaction. No additional source details are available.
            </p>
          </IonText>
        </IonCardContent>
      </IonCard>
    </IonContent>
  );

  const renderContent = () => {
    if (!sourceData) {
      return renderPrivateDetails();
    }

    switch (sourceType) {
      case 'supplier':
        return renderSupplierDetails(sourceData as Supplier);
      case 'buyer':
        return renderBuyerDetails(sourceData as Sale);
      case 'site':
        return renderSiteDetails(sourceData as Ticket | Sale);
      case 'private':
      default:
        return renderPrivateDetails();
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            {sourceType === 'supplier' && 'Supplier Details'}
            {sourceType === 'buyer' && 'Buyer Details'}
            {sourceType === 'site' && 'Platform Details'}
            {sourceType === 'private' && 'Private Transaction'}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      {renderContent()}
    </IonModal>
  );
};

export default SourceDetailsModal;